/*
  Globo de pontos com o Brasil em destaque — seção lcw-globo.

  Mesma ideia do componente RotatingEarth (d3), reescrita sem
  biblioteca: projeção ortográfica feita à mão num <canvas>. Os pontos
  dos continentes já vêm calculados em assets/lcw-globo-dados.json
  (gerados uma vez, fora do site), então o navegador só projeta e
  desenha.

  Comportamento:
  - quando a seção aparece, o globo dá uma volta e para no Brasil;
  - se houver provas, passeia região por região: de São Paulo sai um
    arco até cada prova, e o pin acende quando o arco chega;
  - no fim volta ao Brasil com todas as rotas acesas e fica balançando
    devagar;
  - dá para arrastar (encerra o passeio); ao soltar, volta para o Brasil;
  - fora da tela não desenha nada (economiza bateria);
  - com "reduzir movimento" ligado, fica parado no Brasil com tudo aceso.
*/
(() => {
  if (window.lcwGlobo) return;
  window.lcwGlobo = true;

  const RAD = Math.PI / 180;
  const CENTRO = { lon: -50, lat: -12 };
  const SP = { lon: -46.63, lat: -23.55 };
  const AZUL = '#66aaee';
  const CARBON = '31, 36, 42';
  const semMovimento = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const cache = {};

  // passeio: ordem para o leste, saindo do Brasil e voltando a ele
  const REGIOES = [
    { nome: 'América do Sul', lon: CENTRO.lon, lat: CENTRO.lat }, // o passeio começa onde a volta inicial para
    { nome: 'Europa', lon: 6, lat: 46 },
    { nome: 'África', lon: 24, lat: -26 },
    { nome: 'Ásia', lon: 100, lat: 28 },
    { nome: 'Oceania', lon: 162, lat: -36 },
    { nome: 'América do Norte', lon: 255, lat: 32 }, // -105 + 360: segue girando para o leste
  ];
  const regiaoDe = (lon, lat) =>
    lon < -30 ? (lat < 13 ? 0 : 5) : lon >= 100 && lat < 0 ? 4 : lon >= 45 ? 3 : lat >= 35 ? 1 : 2;

  const INTRO = 2600; // volta inicial
  const MOVER = 1300; // viagem entre regiões
  const PARAR = 2200; // tempo em cada região
  const ARCO = 800; // desenho de cada arco
  const PULSO = 900;

  const carregar = (url) => (cache[url] = cache[url] || fetch(url).then((r) => r.json()));

  const vetor = (lon, lat) => [Math.cos(lat) * Math.cos(lon), Math.cos(lat) * Math.sin(lon), Math.sin(lat)];

  // arco de grande círculo de São Paulo até a prova, subindo acima da superfície
  const montarArco = (lonD, latD) => {
    const a = vetor(SP.lon * RAD, SP.lat * RAD);
    const b = vetor(lonD * RAD, latD * RAD);
    const ang = Math.acos(Math.min(1, Math.max(-1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2])));
    if (ang < 0.01) return null; // a própria São Paulo: só o pin
    const n = Math.max(8, Math.round(ang / RAD / 3));
    const alt = Math.min(0.18, 0.04 + (0.16 * ang) / Math.PI);
    const s = Math.sin(ang);
    const pts = [];
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const ka = Math.sin((1 - t) * ang) / s, kb = Math.sin(t * ang) / s;
      const x = ka * a[0] + kb * b[0], y = ka * a[1] + kb * b[1], z = ka * a[2] + kb * b[2];
      pts.push([Math.atan2(y, x), Math.asin(Math.max(-1, Math.min(1, z))), 1 + alt * Math.sin(Math.PI * t)]);
    }
    return pts;
  };

  const iniciar = (raiz) => {
    if (raiz.dataset.pronto) return;
    raiz.dataset.pronto = '1';
    const canvas = raiz.querySelector('canvas');
    const etiqueta = raiz.querySelector('[data-etiqueta]');
    const chip = raiz.querySelector('[data-regiao]');
    const ctx = canvas.getContext('2d');
    let W = 0, H = 0, R = 0, dados = null, visivel = false, quadro = 0;
    let lon0 = CENTRO.lon + (semMovimento ? 0 : 300), lat0 = CENTRO.lat;
    let inicio = 0, balInicio = 0, arrastando = false, soltouEm = 0, voltaDe = null;

    // provas → arcos e agenda do passeio
    let provas = [];
    try {
      const el = raiz.querySelector('[data-provas]');
      if (el) provas = JSON.parse(el.textContent);
    } catch (e) {
      provas = [];
    }
    const pins = provas.map(([nome, lat, lon]) => ({
      nome,
      lonR: lon * RAD,
      latR: lat * RAD,
      reg: regiaoDe(lon, lat),
      arco: montarArco(lon, lat),
      em: 0, // momento (ms no passeio) em que o arco começa
    }));
    const paradas = REGIOES.map((r, i) => ({ ...r, pins: pins.filter((p) => p.reg === i) })).filter((r) => r.pins.length);
    let fimPasseio = 0;
    {
      let t = 0;
      paradas.forEach((r, k) => {
        r.chega = t + (k === 0 ? 0 : MOVER); // a primeira já começa no Brasil
        const passo = Math.min(140, 1400 / r.pins.length);
        r.pins.forEach((p, j) => { p.em = r.chega + j * passo; });
        t = r.chega + PARAR;
        r.sai = t;
      });
      fimPasseio = t + MOVER; // volta ao Brasil
    }
    let passeioFeito = semMovimento || !pins.length;
    let chipTexto = '';
    const mostrarChip = (texto) => {
      if (!chip || texto === chipTexto) return;
      chipTexto = texto;
      chip.textContent = texto;
      chip.style.opacity = texto ? '1' : '0';
    };
    const resumo = () => mostrarChip(raiz.dataset.resumo || '');
    if (passeioFeito && pins.length) resumo();

    const medir = () => {
      const r = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = r.width; H = r.height; R = Math.min(W, H) * 0.41;
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    // projeção ortográfica: devolve [x, y, profundidade] (profundidade > 0 = lado visível)
    let sp0 = 0, cp0 = 1, l0 = 0;
    const prepara = () => { l0 = lon0 * RAD; const p0 = lat0 * RAD; sp0 = Math.sin(p0); cp0 = Math.cos(p0); };
    const proj = (lonR, latR) => {
      const dl = lonR - l0, cp = Math.cos(latR), sp = Math.sin(latR), cdl = Math.cos(dl);
      return [W / 2 + R * cp * Math.sin(dl), H / 2 - R * (cp0 * sp - sp0 * cp * cdl), sp0 * sp + cp0 * cp * cdl];
    };
    // ponto acima da superfície (raio r); o terceiro valor é a opacidade:
    // a rota vai sumindo perto do horizonte em vez de dar a volta por fora
    const projAlto = (lonR, latR, r) => {
      const [x, y, c] = proj(lonR, latR);
      return [W / 2 + (x - W / 2) * r, H / 2 + (y - H / 2) * r, Math.min(1, Math.max(0, c / 0.35))];
    };

    const linha = (coords, estilo, larg) => {
      ctx.strokeStyle = estilo; ctx.lineWidth = larg; ctx.beginPath();
      let aberto = false;
      for (let i = 0; i < coords.length; i += 2) {
        // coordenadas guardadas em décimos de grau
        const [x, y, c] = proj(coords[i] * 0.1 * RAD, coords[i + 1] * 0.1 * RAD);
        if (c > 0) { aberto ? ctx.lineTo(x, y) : ctx.moveTo(x, y); aberto = true; } else aberto = false;
      }
      ctx.stroke();
    };

    // desenha o arco até a fração f (0 a 1), trecho a trecho com a opacidade
    // de cada ponto; devolve a ponta, se visível
    const tracarArco = (pts, f, alfa) => {
      const ult = f * (pts.length - 1);
      const n = Math.floor(ult);
      let ant = null, ponta = null;
      for (let i = 0; i <= n + 1 && i < pts.length; i++) {
        let p = pts[i];
        if (i > ult) {
          const a = pts[i - 1], k = ult - (i - 1);
          p = [a[0] + (p[0] - a[0]) * k, a[1] + (p[1] - a[1]) * k, a[2] + (p[2] - a[2]) * k];
        }
        const atual = projAlto(p[0], p[1], p[2]);
        if (ant && (ant[2] > 0 || atual[2] > 0)) {
          ctx.globalAlpha = alfa * Math.min(ant[2], atual[2]);
          ctx.beginPath(); ctx.moveTo(ant[0], ant[1]); ctx.lineTo(atual[0], atual[1]); ctx.stroke();
        }
        ant = atual;
        ponta = atual[2] > 0.3 ? atual : null;
      }
      ctx.globalAlpha = 1;
      return ponta;
    };

    // grade a cada 15°, montada uma vez (em décimos de grau, como os dados)
    const grade = [];
    for (let lo = -180; lo < 180; lo += 15) { const l = []; for (let la = -90; la <= 90; la += 3) l.push(lo * 10, la * 10); grade.push(l); }
    for (let la = -75; la < 90; la += 15) { const l = []; for (let lo = -180; lo <= 180; lo += 3) l.push(lo * 10, la * 10); grade.push(l); }

    let tPasseio = 0; // ms desde o começo do passeio
    const desenhar = () => {
      prepara();
      ctx.clearRect(0, 0, W, H);
      ctx.beginPath(); ctx.arc(W / 2, H / 2, R, 0, Math.PI * 2);
      ctx.fillStyle = '#fff'; ctx.fill();
      ctx.strokeStyle = `rgba(${CARBON}, 0.24)`; ctx.lineWidth = 1; ctx.stroke();
      if (!dados) return;

      grade.forEach((g) => linha(g, `rgba(${CARBON}, 0.09)`, 0.6));
      dados.c.forEach((c) => linha(c, `rgba(${CARBON}, 0.27)`, 0.6));

      const esc = R / 215;
      const p = dados.p, b = dados.b;
      for (let i = 0, k = 0; i < p.length; i += 2, k++) {
        const [x, y, c] = proj(p[i] * 0.1 * RAD, p[i + 1] * 0.1 * RAD);
        if (c <= 0) continue;
        const br = b.charCodeAt(k) === 49;
        ctx.fillStyle = br ? AZUL : `rgba(${CARBON}, ${0.24 + 0.35 * c})`;
        ctx.beginPath(); ctx.arc(x, y, (br ? 1.7 : 1.2) * (0.55 + 0.45 * c) * esc, 0, Math.PI * 2); ctx.fill();
      }
      dados.cb.forEach((c) => linha(c, AZUL, 1.2));

      // rotas: as já desenhadas mais claras, a que está sendo traçada mais forte.
      // Ficam dentro do contorno do globo (as rotas longas não saem pela borda)
      ctx.save();
      ctx.beginPath(); ctx.arc(W / 2, H / 2, R * 1.02, 0, Math.PI * 2); ctx.clip();
      ctx.lineCap = 'round';
      pins.forEach((pin) => {
        if (!pin.arco) return;
        const f = passeioFeito ? 1 : Math.min(1, Math.max(0, (tPasseio - pin.em) / ARCO));
        if (f <= 0) return;
        if (f < 1) {
          ctx.strokeStyle = AZUL; ctx.lineWidth = 1.6 * esc;
          const ponta = tracarArco(pin.arco, 1 - Math.pow(1 - f, 2), 1);
          if (ponta) { ctx.fillStyle = AZUL; ctx.beginPath(); ctx.arc(ponta[0], ponta[1], 2.4 * esc, 0, Math.PI * 2); ctx.fill(); }
        } else {
          ctx.strokeStyle = AZUL; ctx.lineWidth = 1.1 * esc;
          tracarArco(pin.arco, 1, 0.5);
        }
      });
      ctx.restore();

      // pins: acendem quando o arco chega, com um pulso
      pins.forEach((pin) => {
        const chegou = passeioFeito ? 1e9 : tPasseio - pin.em - (pin.arco ? ARCO : 0);
        if (chegou < 0) return;
        const [x, y, c] = proj(pin.lonR, pin.latR);
        if (c <= 0.05) return;
        if (chegou < PULSO) {
          const q = chegou / PULSO;
          ctx.strokeStyle = `rgba(102, 170, 238, ${0.6 * (1 - q)})`; ctx.lineWidth = 1.5;
          ctx.beginPath(); ctx.arc(x, y, (3 + 11 * q) * esc, 0, Math.PI * 2); ctx.stroke();
        }
        ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(x, y, 3.4 * esc, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = AZUL; ctx.beginPath(); ctx.arc(x, y, 2.2 * esc, 0, Math.PI * 2); ctx.fill();
      });

      // São Paulo: ponto + etiqueta em HTML (texto nítido e acessível)
      const [sx, sy, sc] = proj(SP.lon * RAD, SP.lat * RAD);
      const mostra = sc > 0.2;
      if (mostra) {
        ctx.fillStyle = 'rgba(102, 170, 238, 0.25)'; ctx.beginPath(); ctx.arc(sx, sy, 11 * esc, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(sx, sy, 6 * esc, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = AZUL; ctx.beginPath(); ctx.arc(sx, sy, 3.5 * esc, 0, Math.PI * 2); ctx.fill();
      }
      if (etiqueta) {
        etiqueta.style.opacity = mostra ? '1' : '0';
        etiqueta.style.transform = `translate(${sx + 16 * esc}px, ${sy}px) translateY(-50%)`;
      }
    };

    const suave = (t) => 1 - Math.pow(1 - t, 3);
    const vaiVem = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const terminarPasseio = (agora) => {
      passeioFeito = true;
      balInicio = agora;
      resumo();
    };
    // posição do globo durante o passeio
    const passear = (t) => {
      let de = { lon: CENTRO.lon, lat: CENTRO.lat };
      for (let k = 0; k < paradas.length; k++) {
        const r = paradas[k];
        if (t < r.chega) {
          const v = vaiVem(Math.min(1, Math.max(0, (t - (r.chega - MOVER)) / MOVER)));
          lon0 = de.lon + (r.lon - de.lon) * v; lat0 = de.lat + (r.lat - de.lat) * v;
          return;
        }
        if (t < r.sai) {
          lon0 = r.lon; lat0 = r.lat;
          const n = r.pins.length;
          mostrarChip(`${r.nome} · ${n} ${n === 1 ? 'prova' : 'provas'}`);
          return;
        }
        de = r;
      }
      // volta ao Brasil, ainda girando para o leste
      const alvo = CENTRO.lon + 360 * Math.ceil((de.lon - CENTRO.lon) / 360);
      const v = vaiVem(Math.min(1, (t - (fimPasseio - MOVER)) / MOVER));
      lon0 = de.lon + (alvo - de.lon) * v; lat0 = de.lat + (CENTRO.lat - de.lat) * v;
    };

    // mantém a longitude perto do Brasil (evita voltas longas ao soltar)
    const normaliza = () => { lon0 = ((((lon0 - CENTRO.lon + 180) % 360) + 360) % 360) - 180 + CENTRO.lon; };

    let ultimo = 0;
    const passo = (agora) => {
      quadro = 0;
      if (!visivel) return;
      if (!inicio) inicio = agora;
      // fora da tela (ou aba escondida) o tempo do passeio não corre
      if (ultimo && agora - ultimo > 100) inicio += agora - ultimo;
      ultimo = agora;
      if (!arrastando) {
        const t = agora - inicio;
        if (t < INTRO) {
          // a volta inicial termina no Brasil
          lon0 = CENTRO.lon + 300 * (1 - suave(t / INTRO));
          balInicio = agora;
        } else if (!passeioFeito) {
          tPasseio = t - INTRO;
          if (tPasseio >= fimPasseio) { normaliza(); lon0 = CENTRO.lon; lat0 = CENTRO.lat; terminarPasseio(agora); }
          else passear(tPasseio);
        } else {
          const balanco = CENTRO.lon + 10 * Math.sin((agora - balInicio) / 4000);
          if (voltaDe) {
            const v = Math.min(Math.max((agora - soltouEm) / 1200, 0), 1);
            lon0 = voltaDe.lon + (balanco - voltaDe.lon) * suave(v);
            lat0 = voltaDe.lat + (CENTRO.lat - voltaDe.lat) * suave(v);
            if (v >= 1) voltaDe = null;
          } else {
            lon0 = balanco; lat0 = CENTRO.lat;
          }
        }
      }
      desenhar();
      quadro = requestAnimationFrame(passo);
    };
    const animar = () => { if (!semMovimento && !quadro) quadro = requestAnimationFrame(passo); };

    // arrastar
    let px = 0, py = 0, lonIni = 0, latIni = 0;
    canvas.addEventListener('pointerdown', (e) => {
      // arrastar encerra o passeio: todas as rotas acendem de uma vez
      if (!passeioFeito) terminarPasseio(performance.now());
      normaliza();
      arrastando = true; px = e.clientX; py = e.clientY; lonIni = lon0; latIni = lat0;
      canvas.setPointerCapture(e.pointerId);
    });
    canvas.addEventListener('pointermove', (e) => {
      if (!arrastando) return;
      lon0 = lonIni - (e.clientX - px) * 0.4;
      lat0 = Math.max(-60, Math.min(60, latIni + (e.clientY - py) * 0.4));
      if (semMovimento) desenhar();
    });
    const soltar = () => {
      if (!arrastando) return;
      normaliza();
      arrastando = false; soltouEm = performance.now() + 1500; voltaDe = { lon: lon0, lat: lat0 };
      // espera um pouco antes de voltar, para a pessoa ver onde parou
      setTimeout(() => { soltouEm = performance.now(); }, 1500);
    };
    canvas.addEventListener('pointerup', soltar);
    canvas.addEventListener('pointercancel', soltar);

    medir(); desenhar();
    new ResizeObserver(() => { medir(); desenhar(); }).observe(canvas);
    new IntersectionObserver((entradas) => {
      visivel = entradas[0].isIntersecting;
      if (visivel && !dados) {
        carregar(raiz.dataset.dados).then((d) => { dados = d; desenhar(); animar(); }).catch(() => {});
      } else if (visivel) animar();
    }, { rootMargin: '100px' }).observe(raiz);
  };

  const todas = () => document.querySelectorAll('[data-lcw-globo]').forEach(iniciar);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', todas);
  else todas();
  document.addEventListener('shopify:section:load', todas);
})();
